/**
 * Response transforms lifted out of the endpoint definitions, for the ones a non-RTK caller also needs.
 *
 * A transform IS the read boundary: past it, preset settings are migrated and conformed, which is what every component, selector and test downstream assumes. A caller that re-implements the unwrap gets data one `conform` behind the app and no error to say so.
 */
import conform from '~/lib/conformSettings'
import { EMPTY_ARRAY } from '~/lib/constants'
import { parsePins } from '~/lib/pinLayer'

/**
 * Narrow a preset RECORD to the fields the cache holds, running its settings through `conform`.
 *
 * The record shape is this module's business; the settings shape is `conform`'s, and `conform` is the only way to reach one. ⚠️ Conforming only on the LOAD path leaves the cached copy unconformed, and `selectPresetChanged` compares live against exactly that copy — so every legacy preset reads dirty the instant it loads.
 *
 * Read-time only: the store record is untouched (see migrations/index.js rule 4).
 */
export const conformPresetSettings = preset => {
	const { id, projectId, name, thumbnailUrl, thumbnailKey } = preset
	return { id, projectId, name, settings: conform(preset.settings), thumbnailUrl, thumbnailKey }
}

/** `getProject`'s read boundary. */
export const transformProject = ({ project, presets }) => {
	// Handle both array format and wrapped format { presets: [...] }
	let presetsArray = Array.isArray(presets) ? presets : presets?.presets || []
	if (presetsArray.length) {
		presetsArray = presetsArray.map(({ id, projectId, name, settings, thumbnailKey, thumbnailUrl }) =>
			conformPresetSettings({
				id,
				projectId,
				name,
				settings,
				thumbnailUrl: thumbnailUrl || null,
				thumbnailKey: thumbnailKey || null,
			})
		)
	}
	return { project: project && { ...project, pins: parsePins(project.pins) }, presets: presetsArray || EMPTY_ARRAY }
}

/** One image from orc (`GET images`, or the `image` of an Ably `image_status`) as the `getImages` cache holds it. */
export const transformImage = ({ id, sessionId, name, createdAt, width, height, size, thumbnailUrl, downloadUrl }) => ({
	id,
	sessionId,
	name: name.replace(/:/g, '_'),
	start: new Date(createdAt).getTime(),
	width,
	height,
	size,
	thumbnailUrl,
	downloadUrl,
})
